import 'react-quill/dist/quill.bubble.css';
import '../styles/InlineQuillEdit.css';

import { Delta, Sources } from 'quill';
import React, {
  KeyboardEvent,
  MouseEvent,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import ReactQuill, { Quill } from 'react-quill';
import { isArray, isEmpty, mergeWith, uniq } from 'lodash';

import { ASSET_TYPE_VALUES } from '../../shared/models/Asset';
import AssetLibrary from '../../shared/components/AssetLibrary/AssetLibrary';
import AssetService from '../../shared/services/AssetService';
import Button from '@mui/material/Button';
import ColorPickerDialog from '../../shared/components/ColorPickerDialog';
import ColorPickerService from '../../shared/services/ColorPickerService';
import LessonComponentService from '../services/LessonComponentService';
import { LessonComponentStyleType } from '../models/LessonComponentStyleType';
import { RgbaColor } from '../models/LessonComponentStyle';
import { nanoid } from 'nanoid';
import sanitizeHtml from 'sanitize-html';
import { useLessonComponentContext } from '../models/LessonComponentContext';
import { useLessonContext } from '../models/LessonContext';

export interface InlineQuillEditProps {
  value: string;
  styleType: LessonComponentStyleType;
  componentKey: number;
  disable?: boolean; //used only in the freetext answer
  placeholder: string;
  isCenterAligned?: boolean;
  notAllowEmptyValue?: boolean;
  /** Handle enter key as submission for 'label' type components
   * (Single selection , multiple selection and slider)
   */
  restrictEnterKey?: boolean;
  onChange: (value: string) => void;
  onClick?: (value: string, id: string) => void;
}

const Embed = Quill.import('blots/embed');
const InlineBlot = Quill.import('blots/inline');

class FillInBlank extends Embed {
  static create(value: any) {
    let node: HTMLElement = super.create(value);
    node.setAttribute('id', value.id);
    node.setAttribute('data-index', `${value.index}`);
    node.setAttribute('component-type', `${value.componentType}`);
    node.textContent = `${
      value.componentType === 'fill_menu' ? 'Selector' : 'Blank'
    } ${value.index}`;
    return node;
  }
  static value(node: HTMLElement): any {
    return {
      id: node.getAttribute('id'),
      index: +node.getAttribute('data-index'),
      componentType: node.getAttribute('component-type')
    };
  }
}

FillInBlank.blotName = 'blank';
FillInBlank.className = 'fill-the-blank';
FillInBlank.tagName = 'blank';

Quill.register(FillInBlank);

class MediaLink extends InlineBlot {
  static create(value: any) {
    let node = super.create();
    node.setAttribute('href', value.url);
    node.setAttribute('target', '_blank');
    node.setAttribute('data-assetkey', value.assetkey);

    return node;
  }

  static formats(node: HTMLElement) {
    return {
      url: node.getAttribute('href'),
      assetkey: node.getAttribute('data-assetkey')
    };
  }
}

MediaLink.blotName = 'mediaLink';
MediaLink.className = 'media-link';
MediaLink.tagName = 'a';

Quill.register(MediaLink);

class NewLine extends Embed {
  static create(value: any) {
    let node: HTMLElement = super.create(value);
    node.innerHTML = '<span style="visibility: hidden">&#x23ce;</span><br/>';
    return node;
  }
}
NewLine.blotName = 'newline';
NewLine.className = 'newline';
NewLine.tagName = 'newline';
Quill.register(NewLine);

interface InlineAttributes {
  [index: string]: string;
}

export function InlineQuillEdit(props: InlineQuillEditProps) {
  const { quillEditing, componentType, onBeforeClick } =
    useLessonComponentContext();
  const [editing, setEditing] = useState(false);
  const [colorPicker, setColorPicker] = useState(false);
  const [color, setColor] = useState('#000000');
  const [showAssetLibrary, setShowAssetLibrary] = useState(false);
  const [assetID, setAssetID] = useState(null);
  const [keyDown, setKeyDown] = useState(false);
  const inputRef = useRef(null);
  const { mode } = useLessonContext();

  const editor = document.getElementById('quill_toolbar_editor');
  const player = document.getElementById('quill_toolbar_player');

  // When there is media-link, update url in 'props.value' with the new sas token
  const contentText = LessonComponentService.getUrlWithSas(props.value || '');

  const [value, setValue] = useState(contentText ? contentText : '');

  /* Removes linespace(<p><br></p>) at the end of html content */
  const filteredContent = LessonComponentService.getFilteredContent(value);

  useEffect(() => {
    if (value !== contentText) {
      setValue(contentText ?? '');
    }
  }, [props.value]);

  useEffect(() => {
    setEditing(quillEditing);
    if (quillEditing) {
      inputRef.current?.focus();
    }
  }, [quillEditing]);

  /** props.onChange() needs to be called only on the first keypress change in the editor value.
   * If there is no value in the editor (ie, after deleting all values) then props.onChange()
   * should be called with 'null'. Basically the props.onChange() sets enable / disable the
   * 'SAVE AND CONTINUE' button in 'LessonPlayer' and 'SAVE LESSON' in 'LESSONEDITOR'.
   */
  useEffect(() => {
    if (editing && filteredContent && keyDown) {
      setKeyDown(false);
      props.onChange(filteredContent);
    }

    if (!filteredContent && (mode === 'preview' || mode === 'learn')) {
      setKeyDown(true);
      props.onChange(null);
    }
  }, [filteredContent, editing]);

  /* blur event listener added for the quill editor and it runs whenever the component is rendered 
  without dependencey array. This is because, when attaching 'onBlur' to ReactQuill component, blur 
  doesn't fire always specifically when clicking the button element after typing in the editor */
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current?.editor.root.addEventListener('blur', handleBlur);
      /* Catching the mousedown on toolbar container and calling e.preventDefault() to fix the 
      toolbar to be visible after clicking the toolbar options specifically after changing the color.
      Reference: https://github.com/quilljs/quill/issues/1290
      */
      inputRef.current?.editor
        .getModule('toolbar')
        .container.addEventListener('mousedown', handleMouseDown);
      return () => {
        inputRef.current?.editor
          .getModule('toolbar')
          .container.removeEventListener('mousedown', handleMouseDown);
        inputRef.current?.editor.root.removeEventListener('blur', handleBlur);
      };
    }
  });

  //Define toolbar text formatting used in different components
  const IU = ['italic', 'underline'];
  const BIU = ['bold', ...IU];
  const SCRIPT = [{ script: 'sub' }, { script: 'super' }];
  const ALIGN = [{ align: '' }, { align: 'center' }, { align: 'right' }];
  const LIST = [{ list: 'ordered' }, { list: 'bullet' }];
  const EXTRA = ['customColor', 'link', 'customLink'];

  const base = [SCRIPT, ALIGN, EXTRA];

  const getContainerFormats = () => {
    switch (props.styleType) {
      case 'h1':
      case 'h2':
        return [IU, ...base];
      case 'p':
        return [BIU, LIST, ...base];
      case 'label':
        return [BIU, SCRIPT, ['customColor']];
      case 'noList':
        return [BIU, ...base];
      case 'noLink':
        return [BIU, SCRIPT, ALIGN, LIST, ['customColor']];
      case 'noLinkList':
        return [BIU, SCRIPT, ALIGN, ['customColor']];
      case 'noAlignList':
        return [BIU, SCRIPT, EXTRA];
      default:
        return null;
    }
  };

  const defaultModules = useMemo(
    () => ({
      toolbar: {
        container: getContainerFormats(),
        handlers: {
          customColor: function () {
            setColorPicker(true);
          },
          customLink: function () {
            //Get the assetkey from selected text if there is any link added and pass it in AssetLibrary.
            //If the selected text doesn't have any link then assetkey will be null.
            const range = inputRef.current.editor?.getSelection();
            const format = inputRef.current.editor?.getFormat(range);
            if (!isEmpty(format) && format.mediaLink) {
              setAssetID(format.mediaLink.assetkey);
            }
            setShowAssetLibrary(true);
          }
        }
      },
      clipboard: {
        matchVisual: false
      }
    }),
    []
  );

  const modules = useMemo(
    () =>
      props.restrictEnterKey
        ? {
            ...defaultModules,
            keyboard: {
              bindings: {
                tab: false,
                noEnterKey: {
                  key: 13,
                  shiftKey: null,
                  altKey: null,
                  metaKey: null,
                  ctrlKey: null,
                  handler: function () {
                    // restrict enter key
                  }
                }
              }
            }
          }
        : {
            ...defaultModules,
            keyboard: {
              bindings: {
                shift_enter: {
                  key: 13,
                  shiftKey: true,
                  handler: (range: any, context: any) => {
                    if (context?.format?.list) {
                      inputRef.current.editor.insertEmbed(
                        range.index,
                        'newline',
                        {}
                      );
                      setEditorIndex();
                      setValue(inputRef.current.editor.root.innerHTML);
                    } else {
                      // Return TRUE to use default handler.
                      return true;
                    }
                  }
                }
              }
            }
          },
    [props.restrictEnterKey]
  );

  const formats = [
    'header',
    'bold',
    'italic',
    'underline',
    'script',
    'align',
    'list',
    'bullet',
    'indent',
    'link',
    'color',
    'blank',
    'newline',
    'mediaLink'
  ];

  const handleClose = (rgba: RgbaColor) => {
    if (rgba) {
      const color = ColorPickerService.rgbToHex(rgba);
      inputRef.current.editor.format('color', color);
      setColor(color);
    }
    setColorPicker(false);
  };

  const options = {
    h1: ['class'],
    h2: ['class'],
    p: ['class'],
    li: ['class'],
    a: ['style', 'rel', 'data-assetkey', 'class', 'href'],
    em: ['style'],
    sup: ['style'],
    sub: ['style'],
    span: ['style'],
    strong: ['style'],
    u: ['style'],
    blank: ['class', 'id', 'data-index', 'component-type'],
    newline: [] as string[]
  };

  const customizer = (
    options: InlineAttributes,
    sanitizeOptions: InlineAttributes
  ) => {
    if (isArray(options)) {
      return uniq(options.concat(sanitizeOptions));
    }
  };

  const mergedAttributes = mergeWith(
    options,
    sanitizeHtml.defaults.allowedAttributes,
    customizer
  );

  const handleClick = (event: MouseEvent<HTMLSpanElement>) => {
    event.preventDefault();
    if (onBeforeClick) {
      onBeforeClick(props.componentKey);
      setEditing(true);
    }
  };

  const setEditorIndex = () => {
    setTimeout(
      () =>
        inputRef.current.editor.setSelection(
          inputRef.current.editor.getSelection().index + 1,
          0
        ),
      0
    );
  };

  const handleMouseDown = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
  };

  const sanitizedValue = (value: string) => {
    return sanitizeHtml(value, {
      allowedTags: sanitizeHtml.defaults.allowedTags.concat([
        'blank',
        'newline'
      ]),
      allowedAttributes: mergedAttributes
    });
  };

  const handleChange = (content: string, delta: Delta, source: Sources) => {
    //When blank is inserted, this shouldn't execute
    const hasBlankInsert = delta.filter((d) => d.insert && d.insert.blank);
    if (!hasBlankInsert.length) {
      if (!filteredContent) {
        /* Sets the placeholder text after the quill editor is instantiated. so that it will display 
        when all the content gets deleted */
        if (inputRef.current) {
          inputRef.current.editor.root.dataset.placeholder = props.placeholder;
        }
      }
      /* set the content in state to allow the new line (eg. list options) */
      setValue(content);
    }
  };

  /** Sanitize the html content before saving into db */
  const handleSubmit = (content: string) => {
    // Retain the previous value when there is no content.
    if (props.notAllowEmptyValue && !content) {
      setValue(contentText);
      props.onChange(sanitizedValue(contentText));
    } else {
      props.onChange(sanitizedValue(content));
    }
  };

  /* props.onChange called in the onBlur to avoid slowness 
  (https://thetarngroup.atlassian.net/browse/B2-1238)  */
  const handleBlur = () => {
    /** The Quill editor 'mouseDown' property returns true when clicking outside of the editor.  
          When copy/paste content into quill editor, the 'mouseDown' value is false so props.onChange 
          won't be called.
      */
    const actualBlur = inputRef.current?.editor.selection.mouseDown;
    if (actualBlur) {
      /** Passing 'content' instead of 'filteredContent' to allow new line when the content
           has list (ordered/un-ordered) added. Passing empty '' when filteredContent is empty.
         */
      const editContent = !filteredContent ? '' : value;
      handleSubmit(editContent);
    }
  };

  const handleFocus = () => {
    inputRef.current?.focus();
    setEditing(true);
  };

  const handleBlankClick = () => {
    const dom = new DOMParser().parseFromString(value, 'text/html');
    const blankIndex = dom.querySelectorAll('.fill-the-blank').length + 1;
    let range = inputRef.current.editor.getSelection();

    const id = nanoid();
    if (range) {
      inputRef.current.editor.insertEmbed(range.index, 'blank', {
        id: id,
        index: blankIndex, // count of existing blanks. Should be taken from component content
        componentType
      });
      setEditorIndex();
      setValue(inputRef.current.editor.root.innerHTML);

      props.onClick(inputRef.current.editor.root.innerHTML, id);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (props.restrictEnterKey && ['Enter', 'Tab'].includes(event.key)) {
      setEditing(false);
      handleSubmit(filteredContent);
    }
  };

  //handle onSelect of custom link to attach the selected file
  const handleCustomLinkSelect = (assetkey: string) => {
    //Focuses the editor as sometimes editor losing focus when clicking custom link button
    inputRef.current?.focus();

    //Find the selected text range and use formatText to add the media link.
    const range = inputRef.current.editor?.getSelection();
    const url = AssetService.getAssetUrl(assetkey);
    inputRef.current.editor.formatText(range.index, range.length, 'mediaLink', {
      url,
      assetkey
    });
  };

  return (
    <>
      <span onClick={handleClick} className={'ql-editor'}>
        {!editing ? (
          <span
            className={`noSelect ${
              filteredContent
                ? ''
                : `placeholder-color ${
                    props.isCenterAligned ? 'placeholder-align' : ''
                  }`
            }`}
            dangerouslySetInnerHTML={{
              // filteredContent is the content without the line space ie. <p><br></p>
              // Displaying original value(including line spaces at end) here not the filteredContent to
              // avoid flickering.
              // Line spaces at the end of content will be removed before storing into db.
              __html: filteredContent || props.placeholder
            }} //value has been sanitized before storing into db.
          ></span>
        ) : (
          <>
            <ReactQuill
              ref={inputRef}
              theme="bubble"
              bounds={editor ? editor : player}
              value={value}
              onChange={handleChange}
              modules={modules}
              formats={formats}
              placeholder={props.placeholder}
              onFocus={handleFocus}
              readOnly={props.disable}
              onKeyDown={handleKeyDown}
            />
            {colorPicker && (
              <ColorPickerDialog
                color={ColorPickerService.hexToRgbA(color)}
                onClose={handleClose}
              />
            )}
          </>
        )}
        {['fill_blank', 'fill_menu'].includes(componentType) &&
          props.styleType === 'noLink' && (
            <div style={{ marginTop: '20px' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleBlankClick}
                disabled={!editing && props.styleType === 'noLink'}
              >
                {`Add ${componentType === 'fill_blank' ? 'blank' : 'selector'}`}
              </Button>
            </div>
          )}
      </span>
      {showAssetLibrary && (
        <AssetLibrary
          types={[...ASSET_TYPE_VALUES]}
          category="author"
          title={'Select a file'}
          selectedAssetkey={assetID}
          onSelect={handleCustomLinkSelect}
          onClose={() => {
            setAssetID(null);
            setShowAssetLibrary(false);
          }}
        />
      )}
    </>
  );
}
